import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { LvfRequestSource } from "@kiraah-shared/common";
import { BehaviorSubject, Observable, from, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { MobileSessionService } from "../auth/mobile-session.service";
import { MobileAuthStore } from "../state/mobile-auth.store";
import { ITokenStorage, TOKEN_STORAGE } from "./token-storage.interface";

const RETRY_HEADER = "x-mobile-retry";
const CONNECTED_IDENTITY_UID_HEADER = "X-CONNECTED-IDENTITY-UID";
const REQUEST_SRC_HEADER = "X-REQUEST-SRC";
const API_CALL_TYPE_HEADER = "X-API-CALL-TYPE";

@Injectable()
export class MobileAuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    @Inject(TOKEN_STORAGE) private readonly tokenStorage: ITokenStorage,
    private readonly sessionService: MobileSessionService,
    private readonly authStore: MobileAuthStore
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return from(this.tokenStorage.get()).pipe(
      switchMap((token) => {
        const requestWithToken = this.withIdentityHeaders(this.withBearerToken(req, token));
        return next.handle(requestWithToken).pipe(
          catchError((error) => this.handle401(error, req, next))
        );
      })
    );
  }

  /**
   * Mirrors web AuthInterceptor: on 401, either start a refresh or queue behind one in progress.
   */
  private handle401(
    error: unknown,
    originalReq: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (
      !(error instanceof HttpErrorResponse) ||
      error.status !== 401 ||
      this.skipUnauthorizedRetry(originalReq.url, originalReq) ||
      originalReq.headers.has(RETRY_HEADER)
    ) {
      return throwError(() => error);
    }

    if (this.isRefreshing) {
      return this.waitForRefreshAndRetry(originalReq, next);
    }

    return this.refreshTokenAndRetry(originalReq, next, error);
  }

  /**
   * Waits for an ongoing token refresh to complete, then retries the request.
   * Same pattern as web AuthInterceptor.waitForRefreshAndRetry.
   */
  private waitForRefreshAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap((token) => {
        const retried = this.withIdentityHeaders(
          this.withBearerToken(
            req.clone({ headers: req.headers.set(RETRY_HEADER, "1") }),
            token
          )
        );
        return next.handle(retried);
      })
    );
  }

  /**
   * Performs token refresh and retries the original request.
   * Same pattern as web AuthInterceptor.refreshTokenAndRetry.
   */
  private refreshTokenAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return from(this.sessionService.refreshTokenIfNeeded()).pipe(
      switchMap(() => from(this.tokenStorage.get())),
      switchMap((token) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(token);

        const retried = this.withIdentityHeaders(
          this.withBearerToken(
            req.clone({ headers: req.headers.set(RETRY_HEADER, "1") }),
            token
          )
        );
        return next.handle(retried);
      }),
      catchError((refreshError) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        if (!this.sessionService.isSessionBootstrapInProgress()) {
          this.sessionService.logout().catch(() => undefined);
        }
        return throwError(() => refreshError);
      })
    );
  }

  private withBearerToken(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    if (!token || this.skipBearerUrl(request.url)) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Adds identity/source headers expected by backend services that resolve the actor
   * from IHeaderParamService (X-CONNECTED-IDENTITY-UID, X-REQUEST-SRC) instead of JWT claims.
   * Mirrors the behavior of the web base.service (which always appends these headers).
   *
   * Public e-signature calls still need {@link withBearerToken} for the gateway opaque-token fallback,
   * but must not mix in connected-identity headers: that path is scoped by X-Public-Token only.
   * CapacitorHttp (native) has been observed to behave differently from desktop XHR when both are sent.
   */
  private withIdentityHeaders(request: HttpRequest<unknown>): HttpRequest<unknown> {
    if (
      this.skipBearerUrl(request.url)
      || this.isPublicScopedRequestUrl(request.url)
      || request.headers.has("X-Public-Token")
    ) {
      return request;
    }

    const headersToSet: Record<string, string> = {};

    // Mobile runtime must always be tagged as MOBILE to avoid inheriting WEB defaults
    // from shared web services that may pre-fill the header.
    headersToSet[REQUEST_SRC_HEADER] = `${LvfRequestSource.MOBILE}`;
    headersToSet[API_CALL_TYPE_HEADER] = `${LvfRequestSource.INTERNAL_API}`;

    const existingIdentityUid = request.headers.get(CONNECTED_IDENTITY_UID_HEADER);
    if (!this.isValidIdentityUid(existingIdentityUid)) {
      const identityUid = this.authStore.authResult()?.ProfilData?.Identity?.Uid;
      if (this.isValidIdentityUid(identityUid)) {
        headersToSet[CONNECTED_IDENTITY_UID_HEADER] = `${identityUid}`;
      }
    }

    if (Object.keys(headersToSet).length === 0) {
      return request;
    }

    return request.clone({ setHeaders: headersToSet });
  }

  /**
   * Routes that must not receive an Authorization header (pre-auth or refresh via cookie).
   */
  private skipBearerUrl(url: string): boolean {
    return (
      /\/api\/master\/authentication\/login(\/|$|\?)/i.test(url) ||
      /\/api\/master\/authentication\/mfa\/verify(\/|$|\?)/i.test(url) ||
      /\/api\/master\/authentication\/select-tenant(\/|$|\?)/i.test(url) ||
      /\/api\/master\/authentication\/otp-login\/request(\/|$|\?)/i.test(url) ||
      /\/api\/master\/authentication\/otp-login\/verify(\/|$|\?)/i.test(url) ||
      /\/api\/master\/authentication\/refresh(\/|$|\?)/i.test(url)
    );
  }

  /**
   * Public e-signature URLs use an opaque X-Public-Token. The gateway validates it as JWT first; when that
   * fails it only accepts the raw token if the caller is authenticated (Bearer). We must still send Bearer
   * here; we only skip refresh on 401 so a bad/expired public token does not trigger logout.
   */
  private isPublicSignatureRequestUrl(url: string): boolean {
    return /\/api\/core\/signaturerequests\/public(\/|$|\?)/i.test(url);
  }

  private isPublicPresenceRequestUrl(url: string): boolean {
    return /\/api\/core\/presence\/public(\/|$|\?)/i.test(url);
  }

  private isPublicLearningCampaignRequestUrl(url: string): boolean {
    return /\/api\/academy\/learning-campaigns\/public(\/|$|\?)/i.test(url);
  }

  private isPublicScopedRequestUrl(url: string): boolean {
    return (
      this.isPublicSignatureRequestUrl(url)
      || this.isPublicPresenceRequestUrl(url)
      || this.isPublicLearningCampaignRequestUrl(url)
    );
  }

  private isValidIdentityUid(value: string | null | undefined): value is string {
    const trimmed = value?.trim();
    return !!trimmed && trimmed !== "undefined" && trimmed !== "null";
  }

  /**
   * Routes where a 401 must not trigger refresh+retry (aligned with web AuthInterceptor auth endpoints + logout).
   */
  private skipUnauthorizedRetry(url: string, request?: HttpRequest<unknown>): boolean {
    if (request?.headers.has("X-Public-Token")) {
      return true;
    }
    if (this.skipBearerUrl(url) || this.isPublicScopedRequestUrl(url)) {
      return true;
    }
    return /\/api\/master\/authentication\/logout\//i.test(url);
  }
}
